import {
  LEGAL_BODY_HTML_FIELD_KEY,
  LEGAL_IMPRINT_CDL_FIELD_KEYS,
  LEGAL_IMPRINT_PDL_FIELD_KEYS,
  LEGAL_PRIVACY_CDL_FIELD_KEYS,
  LEGAL_PRIVACY_PDL_FIELD_KEYS,
} from './legalBlockFields';
import { LAYOUT_COMPONENT_TYPE, type LayoutBlock, type LayoutComponentType } from './studio';
import { SITE_SOCIAL_CONTENT_KEYS } from './socialLinks';

export type FieldDataSource = 'cdl' | 'pdl' | 'odl';

const FOOTER_PDL_KEYS = new Set([
  'phone',
  'email',
  'hours',
  'address',
  'socialLinks',
  ...SITE_SOCIAL_CONTENT_KEYS,
]);
const FOOTER_CDL_KEYS = new Set(['siteTitle', 'legalLine']);

/** Nav hamburger drawer social row — same content keys as footer. */
const NAV_PDL_KEYS = new Set(['socialLinks', ...SITE_SOCIAL_CONTENT_KEYS]);

/** Floating contact channels — phone + social from Local Presence. */
const CONTACT_CHANNELS_PDL_KEYS = new Set([
  'phone',
  'socialLinks',
  ...SITE_SOCIAL_CONTENT_KEYS,
]);

/** Contact page block — NAP lines from Local Presence. */
const CONTACT_BLOCK_PDL_KEYS = new Set(['phone', 'email', 'hours', 'address']);

/** Utility Bar — NAP + social hydrate from Local Presence; Zone 4 edits items (CDL) only. */
const UTILITY_BAR_PDL_KEYS = new Set([
  'phone',
  'email',
  'address',
  'socialLinks',
  ...SITE_SOCIAL_CONTENT_KEYS,
]);

const IMPRINT_PDL = new Set<string>(LEGAL_IMPRINT_PDL_FIELD_KEYS);
const IMPRINT_CDL = new Set<string>([...LEGAL_IMPRINT_CDL_FIELD_KEYS, LEGAL_BODY_HTML_FIELD_KEY]);
const PRIVACY_PDL = new Set<string>(LEGAL_PRIVACY_PDL_FIELD_KEYS);
const PRIVACY_CDL = new Set<string>([...LEGAL_PRIVACY_CDL_FIELD_KEYS, LEGAL_BODY_HTML_FIELD_KEY]);

export function resolveContentFieldSource(
  block: LayoutBlock,
  fieldKey: string,
): FieldDataSource {
  if (fieldKey.startsWith('formFieldLabel:')) {
    return 'odl';
  }

  if (
    fieldKey === 'menuData' &&
    (block.componentType === LAYOUT_COMPONENT_TYPE.menuGrid3Column ||
      block.componentType === LAYOUT_COMPONENT_TYPE.dynamicMenuFlow ||
      block.componentType === LAYOUT_COMPONENT_TYPE.drinksListStatic) &&
    block.dataBinding?.kind === 'menu_catalog'
  ) {
    return 'odl';
  }

  switch (block.componentType as LayoutComponentType) {
    case LAYOUT_COMPONENT_TYPE.footerStandard:
      if (FOOTER_PDL_KEYS.has(fieldKey)) {
        return 'pdl';
      }
      if (FOOTER_CDL_KEYS.has(fieldKey)) {
        return 'cdl';
      }
      break;
    case LAYOUT_COMPONENT_TYPE.navigationHeader:
      if (NAV_PDL_KEYS.has(fieldKey)) {
        return 'pdl';
      }
      break;
    case LAYOUT_COMPONENT_TYPE.contactChannelsFloat:
      if (CONTACT_CHANNELS_PDL_KEYS.has(fieldKey)) {
        return 'pdl';
      }
      break;
    case LAYOUT_COMPONENT_TYPE.contactBlock:
      if (CONTACT_BLOCK_PDL_KEYS.has(fieldKey)) {
        return 'pdl';
      }
      break;
    case LAYOUT_COMPONENT_TYPE.utilityBar:
      if (UTILITY_BAR_PDL_KEYS.has(fieldKey)) {
        return 'pdl';
      }
      break;
    case LAYOUT_COMPONENT_TYPE.legalImprintBlock:
      if (IMPRINT_PDL.has(fieldKey)) {
        return 'pdl';
      }
      if (IMPRINT_CDL.has(fieldKey)) {
        return 'cdl';
      }
      break;
    case LAYOUT_COMPONENT_TYPE.legalPrivacyBlock:
      if (PRIVACY_PDL.has(fieldKey)) {
        return 'pdl';
      }
      if (PRIVACY_CDL.has(fieldKey)) {
        return 'cdl';
      }
      break;
    default:
      break;
  }

  return 'cdl';
}
